// İSG uzmanı portalı — Kontrol aşaması (DepartmentReview) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: Kontrol uçları `[ModuleAction("permits", …)]` + **departman-rol**
// kapısı taşır (kullanıcı yalnız kendi departmanı için; api-contract §31 / brief 095 §3). Bu sonda
// yalnız UX görünürlüğüdür — Kontrol kuyruğunu hafif prob eder (`GET /permits/department-review?
// page=1&pageSize=1`):
//   - 2xx  → erişim var (granted) → Kontrol kabuğu + menü görünür (kullanıcı bir departmana ait)
//   - 403  → yetki/departman yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//     (**backend §31 develop'a henüz merge olmamışsa uç 404 döner → "kullanılamıyor"**; doğru UX,
//     uydurma veri yok — brief 098 §Süreç)
//
// İş İzni (§14 `usePermitsAccess`) sondasından AYRIDIR: aynı `permits` modülü ama Kontrol kuyruğu
// departman-rol kapısıyla ayrı bir yüzeydir (gerçek modül Value = `permits`; probun bağladığı uç
// `permits/department-review` `list` aksiyonudur — fallback null YOK).

import { ApiError, departmentReviewApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { departmentReviewKeys } from './departmentReviewKeys';

export type DepartmentReviewAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface DepartmentReviewAccess {
  status: DepartmentReviewAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Kontrol erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı) varsayılır.
 * 4xx'te retry yok (anında sonuç); 403 (yetki/departman yok) ≠ 404/503 (servis/veri — §31 henüz
 * merge değil dahil) ayrımı korunur.
 */
export function useDepartmentReviewAccess(): DepartmentReviewAccess {
  const query = useQuery({
    queryKey: departmentReviewKeys.access(),
    queryFn: () => departmentReviewApi.listDepartmentReview({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: DepartmentReviewAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
